import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { STATUS_ORDER, STATUS_LABEL, activeTotal } from '../../utils/memberStatus';
import { pct, fmt } from './signupReportUtils';

const TOP_OPTIONS = [10, 20, 50, 100];

const TopNToggle = ({ value, onChange }) => (
  <Box sx={{ display: 'flex', bgcolor: '#fff', border: '1px solid #EAEAEA', borderRadius: '10px', p: '3px' }}>
    {TOP_OPTIONS.map((n) => (
      <Box
        key={n}
        onClick={() => onChange(n)}
        sx={{
          fontSize: 12,
          fontWeight: 600,
          px: 1.5,
          py: 0.7,
          borderRadius: '7px',
          cursor: 'pointer',
          color: value === n ? '#fff' : '#737373',
          bgcolor: value === n ? '#171717' : 'transparent',
        }}
      >
        Top {n}
      </Box>
    ))}
  </Box>
);

const th = {
  textAlign: 'right',
  fontSize: 10.5,
  fontWeight: 700,
  letterSpacing: '.5px',
  textTransform: 'uppercase',
  color: '#737373',
  padding: '11px 10px',
  borderBottom: '1px solid #EAEAEA',
  whiteSpace: 'nowrap',
  // Opaque so body rows scroll underneath the sticky header, not through it.
  background: '#fff',
  position: 'sticky',
  top: 0,
  zIndex: 1,
  // border-collapse drops borders on sticky cells, so redraw the rule as a shadow.
  boxShadow: 'inset 0 -1px 0 #EAEAEA',
};
const thL = { ...th, textAlign: 'left' };
const td = { padding: '11px 10px', borderBottom: '1px solid #F0F0F0', textAlign: 'right', fontVariantNumeric: 'tabular-nums' };
const tdL = { ...td, textAlign: 'left', fontWeight: 600 };

const Cell = ({ n, base, green }) => (
  <td style={td}>
    <span style={{ fontWeight: 600, color: green ? '#16A34A' : 'inherit' }}>{fmt(n)}</span>
    <span style={{ display: 'block', fontSize: 10.5, color: green ? 'rgba(22,163,74,.8)' : '#A3A3A3', fontWeight: 600, marginTop: 2 }}>
      {pct(n, base)}%
    </span>
  </td>
);

const DataRow = ({ name, sub, r }) => (
  <tr>
    <td style={tdL}>
      {name}
      {sub ? <span style={{ fontSize: 10, color: '#A3A3A3', fontWeight: 500, marginLeft: 6 }}>{sub}</span> : null}
    </td>
    <td style={td}><span style={{ fontWeight: 600 }}>{fmt(r.signed)}</span></td>
    <Cell n={activeTotal(r)} base={r.signed} green />
    {STATUS_ORDER.map((k) => (
      <Cell key={k} n={r[k]} base={r.signed} />
    ))}
  </tr>
);

const SignupGeoTable = ({ title, rows = [], nameKey, subKey, totals }) => {
  const [topN, setTopN] = useState(10);
  const sorted = [...rows].sort((a, b) => b.signed - a.signed).slice(0, topN);

  return (
    <Box sx={{ mb: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap', mb: 1.5 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '.6px', textTransform: 'uppercase', color: '#737373' }}>
          {title}
        </Typography>
        <TopNToggle value={topN} onChange={setTopN} />
      </Box>
      {/* Scroll box (both axes) so the sticky header pins to this card while the
          rows scroll under it — the table is 960px wide, so it can't rely on the
          page scroll without forcing a horizontal scrollbar on narrow screens.
          pt:0 is load-bearing: a scroll container clips at its PADDING box, so a
          padding-top would leave a strip above the pinned header that scrolled
          rows paint into. The header cells carry that spacing instead (see `th`). */}
      <Box sx={{ bgcolor: '#fff', border: '1px solid #EAEAEA', borderRadius: '14px', p: 2.2, pt: 0, maxHeight: 'calc(100vh - 250px)', overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 960 }}>
          <thead>
            <tr>
              <th style={thL}>{nameKey === 'city' ? 'City' : 'Country'}</th>
              <th style={th}>Signed Up</th>
              <th style={th} title="Approved + Hidden">Active</th>
              {STATUS_ORDER.map((k) => (
                <th key={k} style={th}>{STATUS_LABEL[k]}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((r, i) => (
              <DataRow key={`${r[nameKey]}-${i}`} name={r[nameKey]} sub={subKey ? r[subKey] : ''} r={r} />
            ))}
            {totals && (
              <tr>
                <td style={{ ...tdL, borderTop: '2px solid #EAEAEA', borderBottom: 'none', fontWeight: 800, paddingTop: 13 }}>Total</td>
                {[
                  { n: totals.signed, base: totals.signed, plain: true },
                  { n: activeTotal(totals), base: totals.signed, green: true },
                  ...STATUS_ORDER.map((k) => ({ n: totals[k], base: totals.signed })),
                ].map((c, idx) => (
                  <td key={idx} style={{ ...td, borderTop: '2px solid #EAEAEA', borderBottom: 'none', fontWeight: 800, paddingTop: 13 }}>
                    <span style={{ color: c.green ? '#16A34A' : 'inherit' }}>{fmt(c.n)}</span>
                    {!c.plain && (
                      <span style={{ display: 'block', fontSize: 10.5, color: c.green ? 'rgba(22,163,74,.8)' : '#A3A3A3', fontWeight: 600, marginTop: 2 }}>
                        {pct(c.n, c.base)}%
                      </span>
                    )}
                  </td>
                ))}
              </tr>
            )}
            {!sorted.length && (
              <tr>
                <td style={{ ...tdL, textAlign: 'center', color: '#A3A3A3' }} colSpan={3 + STATUS_ORDER.length}>No data available</td>
              </tr>
            )}
          </tbody>
        </table>
      </Box>
    </Box>
  );
};

export default SignupGeoTable;
